import { Node } from "@xyflow/react";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { EditCanvasResponseSchema } from "@zapier/service-providers";
import { z } from "zod";
import { abortCurrentStreamSetterAtomFamily } from "../canvasAi";
import { emitAiPredictionUserFeedbackEventSetter } from "../events";
import {
  DEFAULT_NODE_WIDTH,
  IMPLICIT_BAD,
  ZAP_STEP_NODE_HEIGHT,
} from "@zapier/canvas-constants";
import {
  layoutElementsVertically,
  parseAdditionCanvasEditsToNodeData,
} from "../utils";
import { interfacesAppAtom, tablesAppAtom } from "../apps";
import { NodeData, Step, ZapNodeData } from "../types";
import { SUGGESTION } from "../constants";
import { formsTerminologyAtom } from "../diagramming/togglesAtom";

export type AiSuggestedEdits = z.infer<typeof EditCanvasResponseSchema>;

const initialValue = null;

export const aiSuggestedEditsLoadingAtomFamily = atomFamily(
  (nodeId: string) => {
    const baseAtom = atom(false);
    baseAtom.debugLabel = `aiSuggestedEditsLoadingAtomFamily(${nodeId})`;
    return baseAtom;
  },
);

export const isZslExecutionLoadingAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(false);
  baseAtom.debugLabel = `isZslExecutionLoadingAtomFamily(${nodeId})`;
  return baseAtom;
});

export const aiSuggestedEditsAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<AiSuggestedEdits | null>(initialValue);
  baseAtom.debugLabel = `aiSuggestedEditsAtomFamily(${nodeId})`;
  return baseAtom;
});

export const cancelAiSuggestSetterAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(null, (get, set) => {
    const aiSuggestedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
    set(abortCurrentStreamSetterAtomFamily(nodeId));
    set(aiSuggestedEditsAtomFamily(nodeId), null);
    set(aiSuggestedEditsLoadingAtomFamily(nodeId), false);
    set(zslExecutionErrorAtomFamily(nodeId), null);
    set(aiSuggestionErrorAtomFamily(nodeId), null);
    set(emitAiPredictionUserFeedbackEventSetter, {
      prediction_id: aiSuggestedEdits?.additions[0]?.id || "",
      user_rating: IMPLICIT_BAD,
      user_feedback: null,
      correlation_id: null,
    });
  });
  baseAtom.debugLabel = `cancelAiSuggestSetterAtomFamily(${nodeId})`;
  return baseAtom;
});

export const SUGGESTED_NODE_HEIGHT_FALLBACK = 300;

const suggestedNodeHeightsAtom = atom<number[]>([]);
suggestedNodeHeightsAtom.debugLabel = "suggestedNodeHeightsAtom";

export const suggestedNodeHeightsAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom(
    (get) => get(suggestedNodeHeightsAtom),
    (_get, set, heights: number[]) => set(suggestedNodeHeightsAtom, heights),
  );
  baseAtom.debugLabel = `suggestedNodeHeightsAtomFamily(${nodeId})`;
  return baseAtom;
});

export type ZslExecutionError = {
  message: string;
  type: "error" | "warning";
  zsl?: string;
  prompt?: string;
};

export type AiSuggestionError = {
  message: string;
  error?: unknown;
};

export const aiSuggestionErrorAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<AiSuggestionError | null>(null);
  baseAtom.debugLabel = `aiSuggestionErrorAtomFamily(${nodeId})`;
  return baseAtom;
});

export const zslExecutionErrorAtomFamily = atomFamily((nodeId: string) => {
  const baseAtom = atom<ZslExecutionError | null>(null);
  baseAtom.debugLabel = `zslExecutionErrorAtomFamily(${nodeId})`;
  return baseAtom;
});

export const suggestedNodeAtomFamily = atomFamily((suggestedNodeId: string) => {
  const baseAtom = atom((get) => {
    // Return null if not a suggestion node
    if (!suggestedNodeId.startsWith(`${SUGGESTION}-`)) return null;

    // Get the real nodeId from the suggestedNodeId
    const sourceNodeId = suggestedNodeId.split("::")[1];

    // Get suggestions for the real node
    const suggestion = get(aiSuggestedEditsAtomFamily(sourceNodeId));
    if (!suggestion) return null;

    // Parse the suggestedNodeId to get additionIndex and nodeIndex
    const [, additionIndex, nodeIndex] = suggestedNodeId.split("-");
    if (!additionIndex || !nodeIndex) return null;

    // Get the addition at the specified index
    const addition = suggestion.additions[parseInt(additionIndex)];
    if (!addition) return null;

    // Parse the addition to get node data
    const tablesApp = get(tablesAppAtom);
    const interfacesApp = get(interfacesAppAtom);
    const interfacesTerms = get(formsTerminologyAtom);

    if (interfacesApp) {
      interfacesApp.name = interfacesTerms.byZapier;
    }

    const nodes = parseAdditionCanvasEditsToNodeData(addition, {
      tablesApp: tablesApp!,
      interfacesApp: interfacesApp!,
    }) as Array<Node>;

    const suggestedNode = nodes[parseInt(nodeIndex)] as Node<NodeData>;

    // Return the node data at the specified index
    return suggestedNode
      ? ({
          ...suggestedNode,
          id: suggestedNodeId.split("::")[0],
          // need this for layoutElementsVertically
          position: {
            x: 0,
            y: 0,
          },
          // need this for layoutElementsVertically
          measured: {
            width: DEFAULT_NODE_WIDTH,
            height: 300,
          },
        } as Node<NodeData>)
      : undefined;
  });

  baseAtom.debugLabel = `suggestedNodeAtomFamily(${suggestedNodeId})`;
  return baseAtom;
});

export const suggestedZapStepsAndEdgesAtomFamily = atomFamily(
  (suggestedNodeId: string) => {
    const baseAtom = atom((get) => {
      // Return null if not a suggestion node
      if (!suggestedNodeId.startsWith(`${SUGGESTION}-`)) return null;

      const suggestedZapNode = get(
        suggestedNodeAtomFamily(suggestedNodeId),
      ) as Node<ZapNodeData>;

      if (!suggestedZapNode) return null;

      const { zapId } = suggestedZapNode.data as ZapNodeData;
      if (!zapId) return null;

      const suggestedNodeIdFirstPart = suggestedNodeId.split("::")[0];

      // Get the real nodeId from the suggestedNodeId
      const sourceNodeId = suggestedNodeId.split("::")[1];

      // Get suggestions for the real node
      const suggestion = get(aiSuggestedEditsAtomFamily(sourceNodeId));
      if (!suggestion) return null;

      // Find the zap in zsl.zaps that matches our zapId
      const zap = suggestion.additions[0]?.zsl?.zaps?.find(
        (z) => z.id === zapId,
      );
      if (!zap) return null;

      const zapStepNodes = zap.zdl.steps.map((step: Step, i: number) => {
        return {
          type: "zapStep",
          id: `zap-${suggestedNodeIdFirstPart}-${step.id}`,
          data: {
            stepNumber: i + 1,
            step,
            isZapPublished: false,
          },
          position: {
            x: 0,
            y: 0,
          },
          measured: {
            width: DEFAULT_NODE_WIDTH,
            height: ZAP_STEP_NODE_HEIGHT,
          },
          parentId: suggestedNodeId.split("::")[0],
          selectable: false, // we don't want multi-select panel to count zap steps, just the zap node
          deletable: false,
          draggable: false,
          connectable: false,
        };
      });

      const zapStepEdges = zapStepNodes.map((node: Node, i: number) => {
        const source = i === 0 ? node.parentId : zapStepNodes[i - 1].id;
        const target = node.id;
        const sourceHandle =
          i === 0 ? "zap-node-bottom-handle" : "zap-step-bottom-handle";
        const targetHandle = "zap-step-top-handle";

        return {
          id: `${source}=>${target}`,
          type: "zapStepEdge",
          source,
          target,
          sourceHandle,
          targetHandle,
          deletable: false,
          focusable: false,
          selectable: false,
          reconnectable: false,
          animated: true,
        };
      });

      const { layoutedZapSteps, layoutedZapStepEdges } =
        layoutElementsVertically({
          zapNode: suggestedZapNode,
          zapSteps: zapStepNodes,
          zapStepEdges,
        });

      return {
        nodes: [suggestedZapNode, ...layoutedZapSteps],
        edges: layoutedZapStepEdges,
      };
    });

    baseAtom.debugLabel = `suggestedZapStepsAndEdgesAtomFamily(${suggestedNodeId})`;
    return baseAtom;
  },
);
